import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';
import { SpinnerGap, Target, Users, ChartLineUp } from '@phosphor-icons/react';
import PersonalTab from './objectives/PersonalTab';
import NetworkTab from './objectives/NetworkTab';
import ProjectionTab from './objectives/ProjectionTab';

const TABS = [
  { id: 'pessoal', label: 'Previsões Pessoais', icon: Target },
  { id: 'rede', label: 'Previsões de Rede', icon: Users },
  { id: 'projecao', label: 'Projeção de Objetivos', icon: ChartLineUp },
];

const EMPTY_FORM = () => ({
  ano: new Date().getFullYear(),
  mes: new Date().getMonth() + 1,
  volume_pessoal: '', volume_kits: '', volume_clientes: '', num_diretos_novos: '',
});

export default function ObjectivesPage() {
  const [tab, setTab] = useState('pessoal');
  const [forecasts, setForecasts] = useState([]);
  const [networkForecasts, setNetworkForecasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [rf, setRf] = useState({ consultor: '', ano: '', mes: '' });
  const [consultoresRede, setConsultoresRede] = useState([]);
  const [realByMonth, setRealByMonth] = useState({});
  const [diretosRealByMonth, setDiretosRealByMonth] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, n, cr, pbm] = await Promise.all([
        api.get('/dashboard/forecasts'),
        api.get('/dashboard/forecastsNetwork'),
        api.get('/rede/consultoresArvore'),
        api.get('/dashboard/personalBillingByMonth'),
      ]);
      if (p.data?.success) setForecasts(p.data.data || []);
      if (n.data?.success) setNetworkForecasts(n.data.data || []);
      // Real directos por ano-mês (key "Y-M" para evitar colisões entre anos)
      const dirMap = {};
      (p.data?.directosReal || []).forEach(row => { dirMap[`${row.Ano}-${row.Mes}`] = row.N; });
      setDiretosRealByMonth(dirMap);
      setConsultoresRede(cr.data?.data || []);
      const map = {};
      (pbm.data?.data || []).forEach(row => { map[row.Mes] = Number(row.pessoal || 0) + Number(row.clientes || 0); });
      setRealByMonth(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openEdit = (f) => {
    setEditId(f.ID_Forecast);
    setForm({
      ano: f.Ano, mes: f.Mes,
      volume_pessoal: f.VolumePessoal_P || 0,
      volume_kits: f.VolumeKits_P || 0,
      volume_clientes: f.VolumeClientes_P || 0,
      num_diretos_novos: f.NumDiretosNovos_P || 0,
    });
    setShowForm(true);
    setResult(null);
  };

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM());
    setShowForm(true);
    setResult(null);
  };

  const handleSave = async () => {
    setSaving(true); setResult(null);
    const endpoint = editId ? '/dashboard/forecasts/edit' : '/dashboard/forecasts/add';
    const body = editId ? { id_forecast: editId, ...form } : form;
    try {
      const { data } = await api.post(endpoint, body);
      setResult(data);
      if (data.success) { setShowForm(false); setEditId(null); loadData(); }
    } catch {
      setResult({ success: false, message: 'Erro de comunicação.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta previsão?')) return;
    try {
      const { data } = await api.post('/dashboard/forecasts/delete', { id_forecast: id });
      if (data.success) loadData();
    } catch (e) { console.error(e); }
  };

  // ── Projections derived from personal forecasts + real invoicing ──
  const projections = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearForecasts = forecasts.filter(f => f.Ano === currentYear);
    const MONTHS = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const monthly = Array.from({ length: 12 }, (_, i) => {
      const f = yearForecasts.find(x => x.Mes === i + 1);
      const storedR = Number(f?.VolumePessoal_R || 0) + Number(f?.VolumeKits_R || 0) + Number(f?.VolumeClientes_R || 0);
      const realTotal = (realByMonth[i + 1] != null) ? Number(realByMonth[i + 1] || 0) : storedR;
      return {
        mes: i + 1, label: MONTHS[i + 1],
        total: Number(f?.VolumePessoal_P || 0) + Number(f?.VolumeKits_P || 0) + Number(f?.VolumeClientes_P || 0),
        realTotal,
      };
    });
    const quarters = [
      { label: 'T1 (Jan-Mar)', months: [0, 1, 2] },
      { label: 'T2 (Abr-Jun)', months: [3, 4, 5] },
      { label: 'T3 (Jul-Set)', months: [6, 7, 8] },
      { label: 'T4 (Out-Dez)', months: [9, 10, 11] },
    ].map(q => ({
      ...q,
      previsto: q.months.reduce((s, i) => s + monthly[i].total, 0),
      real: q.months.reduce((s, i) => s + monthly[i].realTotal, 0),
    }));
    const annual = {
      previsto: monthly.reduce((s, m) => s + m.total, 0),
      real: monthly.reduce((s, m) => s + m.realTotal, 0),
    };
    return { monthly, quarters, annual };
  }, [forecasts, realByMonth]);

  if (loading) return (
    <div className="flex justify-center items-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>
  );

  return (
    <div data-testid="objectives-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">Objetivos</h1>
        <p className="text-sm text-[#6B7280] mt-1">Defina objetivos mensais e acompanhe a projeção</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 overflow-x-auto -mx-3 sm:mx-0">
        <div className="inline-flex gap-1 bg-[#F3F4F6] rounded-lg p-1 mx-3 sm:mx-0 whitespace-nowrap" data-testid="obj-tabs">
          {TABS.map(t => (
            <button key={t.id} data-testid={`obj-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-all flex-shrink-0 ${
                tab === t.id ? 'bg-white text-[#0F1829] shadow-sm' : 'text-[#6B7280] hover:text-[#111827]'
              }`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'pessoal' && (
        <PersonalTab
          forecasts={forecasts}
          realByMonth={realByMonth}
          diretosRealByMonth={diretosRealByMonth}
          showForm={showForm}
          form={form}
          setForm={setForm}
          editId={editId}
          saving={saving}
          result={result}
          onNew={openNew}
          onEdit={openEdit}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditId(null); }}
          onDelete={handleDelete}
        />
      )}
      {tab === 'rede' && <NetworkTab networkForecasts={networkForecasts} consultoresRede={consultoresRede} rf={rf} setRf={setRf} />}
      {tab === 'projecao' && <ProjectionTab projections={projections} />}
    </div>
  );
}
